import { X, Mail, User, Calendar, Phone, MapPin, Briefcase, Users, Home, FileText, ChevronDown, ShieldCheck, Check } from 'lucide-react';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addPatient, lookupPatientHousehold, addHouseholdMember } from '../../redux/patientSlice';
import { checkEmailExists, checkMobileExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import { lookupMocLink, classifyMocLookup } from '../../redux/mocLinkSlice';
import MocLinkModal from '../moc/MocLinkModal';
import HouseholdPicker from '../household/HouseholdPicker';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { docPatCreationCountryList } from '../../redux/masterSlice';
import CountryTimeZoneSelect from '../common/CountryTimeZoneSelect';
import GenderSelect from '../common/GenderSelect';
import PhoneCountryDropdown from '../common/PhoneCountryDropdown';
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';
import { patientPolicyFor, isConnectedTenant, policyErrorMessage, policyErrorField, isAnyOneContact, missingAnyContact, otpVerificationPending } from '../../utils/loginPolicy';
import baseURL from '../../utils/baseURL';

// CONNECTED tenants (owner, 2 Oct 2026): the desk owes no code — the patient
// verifies at their first sign-in — and "Send OTP" stays only as an OPTION for
// a patient who is with you now. Drawn as a quiet outline chip labelled
// "Verify (optional)" so it no longer reads as a demand (owner, 20 Oct 2026).
// An isolated hospital whose policy does demand the code keeps the solid button.
const OPTIONAL_OTP_CLASS = 'ml-2 shrink-0 text-xs px-2 py-1 rounded-lg border border-[#4ABDBD]/60 bg-white text-[#2D8A8A] font-medium hover:bg-[#4ABDBD]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

export default function AddPatient({ isOpen, onClose, onSubmit, onOpenPatient }) {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation(['addPatient']);
    const userRole = localStorage.getItem('Role');
    // WHOSE list the green line can claim the patient is on. A doctor filling
    // this form is adding to their OWN list, so the lookup can be asked whether
    // the matched record is already theirs; an admin or staff member is adding
    // to the hospital's, with no doctor in play — the answer stays "not asked"
    // and the line says what is actually true, that a record exists here.
    const ownDoctorId = userRole === 'doctor' ? (localStorage.getItem('ID') || null) : null;
    const { isLoading: patientLoading } = useSelector((state) => state.patient);
    const { emailCheckLoading, mobileCheckLoading, sendOtpLoading, verifyOtpLoading } = useSelector((state) => state.auth);
    const { countryTimezoneList } = useSelector((state) => state.master);
    const axiosInstance = useAxiosAuth();

    // THE PATIENT RULE OF THIS HOSPITAL (the creating staff's own tenant)
    // decides which identifiers are mandatory here — /tenants/public's
    // `patientPolicy` (utils/loginPolicy#patientPolicyFor).
    //
    // CONNECTED (owner, 2 Oct 2026): a Mitra One Care patient needs AT LEAST
    // ONE contact, e-mail or mobile, and the desk proves none of them — the
    // patient verifies their contacts and chooses a password at their first
    // sign-in. The optional Send OTP stays: a code checked here with the
    // patient present spares them that step, and its proof rides along.
    // ISOLATED: the hospital's own login policy, exactly as before — a
    // mandated-but-skipped OTP does not block saving, the desk confirms
    // "create as pending" and the patient proves ownership at first sign-in.
    const [publicTenants, setPublicTenants] = useState([]);
    useEffect(() => {
        if (!isOpen) return;
        let alive = true;
        (async () => {
            try {
                const res = await axiosInstance.get(`${baseURL}/tenants/public`);
                if (alive) setPublicTenants(res.data?.tenants || []);
            } catch {
                if (alive) setPublicTenants([]);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    // Until the list answers, the connection mode useTenantFeatures cached
    // for this tenant says which world the form is in.
    const cachedConnected = (() => {
        try { return localStorage.getItem('tenantConnectionMode') === 'connected'; } catch { return false; }
    })();
    const connected = isConnectedTenant(publicTenants, localStorage.getItem('TenantId') || '', cachedConnected);
    const loginPolicy = patientPolicyFor(publicTenants, localStorage.getItem('TenantId') || '', cachedConnected);
    const [emailStatus, setEmailStatus] = useState(null); // null: not checked, true: available, false: exists
    const emailCheckTimeoutRef = useRef(null);
    const lastCheckedEmailRef = useRef(''); // Track last checked email
    const isEmailCheckInProgressRef = useRef(false); // Track if email check is in progress
    const [phoneStatus, setPhoneStatus] = useState(null); // null: not checked, true: available, false: exists
    const phoneCheckTimeoutRef = useRef(null);
    const lastCheckedPhoneRef = useRef(''); // Track last checked phone number
    const isPhoneCheckInProgressRef = useRef(false); // Track if phone check is in progress
    // Currency tokens for the debounced checks: each run bails out after every
    // await when a newer run has started or the field moved on, so a slow,
    // superseded answer can never set the status / open the MOC popup for a
    // value that is no longer in the field.
    const emailRunRef = useRef(0);
    const phoneRunRef = useRef(0);

    // ADDING A PATIENT AT A CONNECTED HOSPITAL IS A SEARCH, THEN AN
    // ALLOCATION (doc/one-patient-row.md §1, §6).
    //
    // A patient belongs to Mitra One Care, not to a hospital: one human is ONE
    // `patients` row, and this hospital does not copy it — it gets its own
    // patient number for them. So the identifier the desk types has exactly
    // three answers:
    //   (a) they ALREADY HAVE A NUMBER HERE — not a question at all. Confirmed
    //       inline (`inTenantMatch`, a green line) and that patient is opened
    //       without a click.
    //   (b) they have a MITRA ONE CARE PROFILE but no number here — the popup
    //       (components/moc/MocLinkModal.jsx) asks them to consent, and the
    //       server allocates. Nothing is created.
    //   (c) nobody matches — saving creates the PERSON, unclaimed, plus this
    //       hospital's allocation. It stays editable here until they sign in.
    // The old fourth state — a record here belonging to no account — cannot
    // occur any more and is gone with the orphan repair tab.
    // `emailBlock`/`phoneBlock` say WHY a field is blocked.
    const tenantId = localStorage.getItem('TenantId') || '';
    const [emailBlock, setEmailBlock] = useState(null); // 'doctor'|'account'|'moc'|'pending'
    const [phoneBlock, setPhoneBlock] = useState(null);
    const [inTenantMatch, setInTenantMatch] = useState(null); // { source: 'email'|'phone', patient }
    const inTenantHandoffRef = useRef(null);
    const [mocPopup, setMocPopup] = useState(null);     // { lookup, source: 'email'|'phone' }
    // CASE (c), SAID OUT LOUD. A connected hospital's lookup that matches
    // nobody means this form is about to create a PERSON in Mitra One Care —
    // not a record of its own — and that person is UNCLAIMED until they sign
    // in to the patient app, which is the window in which this desk may still
    // correct what it typed. The desk was never told either half, so a typo
    // looked repairable forever and the new profile looked like a local row.
    // Only set from a connected lookup: at an isolated clinic nothing about
    // Mitra One Care is true, and the form must read exactly as it always did.
    const [unclaimedNotice, setUnclaimedNotice] = useState(false);
    // ONE CONTACT, SEVERAL PATIENTS (owner, 17 Sep 2026). Anitha's e-mail is
    // also her three children's login — secondary rows on her household. Case
    // (a) above used to open THE one row the lookup returned, which is the
    // wrong person whenever the desk meant a child. When the contact turns out
    // to be a household the form asks who instead, and creates nothing: every
    // row on the card already exists, so the only outcomes are "open that
    // profile" or "add a new member to this household".
    const [household, setHousehold] = useState(null);
    const [householdPick, setHouseholdPick] = useState(null);
    const [householdAdding, setHouseholdAdding] = useState(false);
    const [householdAddError, setHouseholdAddError] = useState('');
    const [identifierBusy, setIdentifierBusy] = useState({ email: false, phone: false });
    const [recheckTick, setRecheckTick] = useState(0);
    const lastLookupRef = useRef({ email: null, phone: null });
    const suppressPopupRef = useRef(false);
    const emailBusy = emailCheckLoading || identifierBusy.email;
    const phoneBusy = mobileCheckLoading || identifierBusy.phone;

    // --- Email OTP state ---
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [emailOtpCode, setEmailOtpCode] = useState('');
    const [emailOtpVerified, setEmailOtpVerified] = useState(false);
    const [emailOtpError, setEmailOtpError] = useState('');
    const [emailOtpTimer, setEmailOtpTimer] = useState(0);
    const emailOtpTimerRef = useRef(null);
    const emailOtpVerifyingRef = useRef(false);

    // --- Phone OTP state ---
    const [phoneOtpSent, setPhoneOtpSent] = useState(false);
    const [phoneOtpCode, setPhoneOtpCode] = useState('');
    const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
    const [phoneOtpError, setPhoneOtpError] = useState('');
    const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
    const phoneOtpTimerRef = useRef(null);
    const phoneOtpVerifyingRef = useRef(false);

    // OTP ownership proofs (verify-otp -> proof.token). The token — not a
    // client-side boolean — is the only thing the create endpoint trusts.
    const [emailProof, setEmailProof] = useState(null);
    const [phoneProof, setPhoneProof] = useState(null);

    // Store dispatch and axiosInstance in refs to avoid dependency issues
    const dispatchRef = useRef(dispatch);
    const axiosInstanceRef = useRef(axiosInstance);

    // Update refs when values change
    useEffect(() => {
        dispatchRef.current = dispatch;
        axiosInstanceRef.current = axiosInstance;
    }, [dispatch, axiosInstance]);

    useEffect(() => {
        dispatch(docPatCreationCountryList());
    }, [dispatch]);

    // Proper email regex: allows letters, numbers, dots, hyphens, underscores, and plus signs in local part
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        gender: '',
        dob: '',
        occupation: '',
        email: '',
        phone: '',
        altPhone: '',
        address: '',
        additional: '',
        countryId: '',
        timeZone: '',
        city: '',
        zipCode: '',
        age: '',
    });
    const [errors, setErrors] = useState({});
    const selectedCountry = countryTimezoneList?.find(c => c.countryId === formData.countryId) || null;

    const [apiError, setApiError] = useState(null);
    const [charLimitReached, setCharLimitReached] = useState({});

    // Debounced email check
    useEffect(() => {
        if (!isOpen) return; // Don't check if modal is closed

        // Clear previous timeout
        if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
        }

        const email = formData.email?.trim() || '';

        // If email is empty or invalid format, reset status
        if (!email || !emailRegex.test(email)) {
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            return;
        }

        // If email hasn't changed or check is in progress, don't check again
        if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        emailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = formData.email.trim();

            // Double check email is still valid and hasn't changed during debounce
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked email BEFORE making the API call
            isEmailCheckInProgressRef.current = true;
            lastCheckedEmailRef.current = trimmedEmail;
            const run = ++emailRunRef.current;
            // Stale when a newer run has started OR the field/caller invalidated
            // this value (handleChange, the X button and handleMocPopupClose all
            // reset lastCheckedEmailRef to '').
            const stale = () => run !== emailRunRef.current || lastCheckedEmailRef.current !== trimmedEmail;

            setIdentifierBusy((b) => ({ ...b, email: true }));
            try {
                // 1) A doctor/staff login with this email at THIS hospital still
                //    blocks — the MOC lookup only knows patients. tenantId makes
                //    the answer per-hospital instead of platform-wide.
                const existAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    tenantId,
                    axiosInstance: axiosInstanceRef.current
                }));
                if (stale()) return;
                const exists = checkEmailExists.fulfilled.match(existAction) ? existAction.payload : null;
                if (exists?.exists && exists.role && exists.role !== 'patient') {
                    setEmailStatus(false);
                    setEmailBlock(exists.role === 'doctor' ? 'doctor' : 'account');
                    return;
                }

                // 2) Tenant-aware lookup: registered HERE / has a Mitra One Care
                //    account / a link request already pending → popup; nothing
                //    → free, create as today.
                const lookupAction = await dispatchRef.current(lookupMocLink({ email: trimmedEmail, doctorId: ownDoctorId }));
                if (stale()) return;
                if (lookupMocLink.fulfilled.match(lookupAction)) {
                    const kind = classifyMocLookup(lookupAction.payload);
                    if (kind === 'inTenant') {
                        // ONE CONTACT, SEVERAL PATIENTS: before opening "the"
                        // matching record, ask whether this contact is a whole
                        // household and let the desk say which member.
                        const hh = await tryHousehold('email', { email: trimmedEmail });
                        if (stale()) return;
                        if (hh) { showHousehold(hh); return; }
                        // Already a record here — confirm it in green and open
                        // that patient; no popup, no red "already exists".
                        applyInTenantMatch('email', lookupAction.payload.inTenant);
                    } else if (kind) {
                        setUnclaimedNotice(false);
                        setEmailStatus(false);
                        setEmailBlock(kind);
                        openMocPopup('email', lookupAction.payload);
                    } else if (exists?.exists) {
                        // A login exists here without a patient row — creating
                        // would collide, so keep the plain "already exists".
                        setEmailStatus(false);
                        setEmailBlock('account');
                    } else {
                        setEmailStatus(true);
                        setEmailBlock(null);
                        setUnclaimedNotice(Boolean(lookupAction.payload?.tenantConnected));
                    }
                } else if (exists) {
                    // Old backend without /moc-link/lookup: per-tenant answer only.
                    if (exists.exists) {
                        const hh = await tryHousehold('email', { email: trimmedEmail });
                        if (stale()) return;
                        if (hh) { showHousehold(hh); return; }
                    }
                    setEmailStatus(!exists.exists);
                    setEmailBlock(exists.exists ? 'account' : null);
                } else {
                    setEmailStatus(null);
                    lastCheckedEmailRef.current = ''; // Reset on error
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (stale()) return;
                setEmailStatus(null);
                lastCheckedEmailRef.current = ''; // Reset on error
            } finally {
                // Only the CURRENT run owns the in-progress flag and the spinner;
                // a superseded run must not hide the newer run's busy state.
                // (Token only — the error branches legitimately reset the ref.)
                if (run === emailRunRef.current) {
                    isEmailCheckInProgressRef.current = false;
                    setIdentifierBusy((b) => ({ ...b, email: false }));
                }
            }
        }, 500);

        // Cleanup function
        return () => {
            if (emailCheckTimeoutRef.current) {
                clearTimeout(emailCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.email, isOpen, recheckTick]);

    // Debounced phone check (only for main phone, not altPhone)
    useEffect(() => {
        if (!isOpen) return;

        if (phoneCheckTimeoutRef.current) {
            clearTimeout(phoneCheckTimeoutRef.current);
        }

        const phone = formData.phone?.trim() || '';

        const minLen = selectedCountry?.mobileMinLength || 10;
        const maxLen = selectedCountry?.mobileMaxLength || 10;
        const isValidPhone = selectedCountry && new RegExp(`^\\d{${minLen},${maxLen}}$`).test(phone);

        if (!phone || !isValidPhone) {
            setPhoneStatus(null);
            lastCheckedPhoneRef.current = '';
            return;
        }

        if (lastCheckedPhoneRef.current === phone || isPhoneCheckInProgressRef.current) {
            return;
        }

        phoneCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedPhone = formData.phone.trim();

            const isStillValid = selectedCountry && new RegExp(`^\\d{${selectedCountry.mobileMinLength || 10},${selectedCountry.mobileMaxLength || 10}}$`).test(trimmedPhone);

            if (!trimmedPhone || !isStillValid || lastCheckedPhoneRef.current === trimmedPhone || isPhoneCheckInProgressRef.current) {
                return;
            }

            isPhoneCheckInProgressRef.current = true;
            lastCheckedPhoneRef.current = trimmedPhone;
            const run = ++phoneRunRef.current;
            // Same currency guard as the email check above.
            const stale = () => run !== phoneRunRef.current || lastCheckedPhoneRef.current !== trimmedPhone;

            setIdentifierBusy((b) => ({ ...b, phone: true }));
            try {
                // Tenant-aware lookup (see the email check above). The old
                // platform-wide mobileNoExist no longer blocks here.
                const lookupAction = await dispatchRef.current(lookupMocLink({ phone: trimmedPhone, doctorId: ownDoctorId }));
                if (stale()) return;
                if (lookupMocLink.fulfilled.match(lookupAction)) {
                    const kind = classifyMocLookup(lookupAction.payload);
                    if (kind === 'inTenant') {
                        // Same household question as the email check above —
                        // one mobile can be a mother's and her children's alike.
                        const hh = await tryHousehold('phone', { phone: trimmedPhone });
                        if (stale()) return;
                        if (hh) { showHousehold(hh); return; }
                        // Same as the email check above — hand over, don't block.
                        applyInTenantMatch('phone', lookupAction.payload.inTenant);
                    } else if (kind) {
                        setUnclaimedNotice(false);
                        setPhoneStatus(false);
                        setPhoneBlock(kind);
                        openMocPopup('phone', lookupAction.payload);
                    } else {
                        setPhoneStatus(true);
                        setPhoneBlock(null);
                        setUnclaimedNotice(Boolean(lookupAction.payload?.tenantConnected));
                    }
                } else {
                    // Old backend without /moc-link/lookup: per-tenant answer only.
                    const resultAction = await dispatchRef.current(checkMobileExists({
                        phone: trimmedPhone,
                        tenantId,
                        axiosInstance: axiosInstanceRef.current
                    }));
                    if (stale()) return;
                    if (checkMobileExists.fulfilled.match(resultAction)) {
                        if (resultAction.payload.exists) {
                            const hh = await tryHousehold('phone', { phone: trimmedPhone });
                            if (stale()) return;
                            if (hh) { showHousehold(hh); return; }
                        }
                        setPhoneStatus(!resultAction.payload.exists);
                        setPhoneBlock(resultAction.payload.exists ? 'account' : null);
                    } else {
                        setPhoneStatus(null);
                        lastCheckedPhoneRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking phone:', error);
                if (stale()) return;
                setPhoneStatus(null);
                lastCheckedPhoneRef.current = '';
            } finally {
                // Token only: a superseded run must not clear the newer run's
                // in-progress flag / spinner.
                if (run === phoneRunRef.current) {
                    isPhoneCheckInProgressRef.current = false;
                    setIdentifierBusy((b) => ({ ...b, phone: false }));
                }
            }
        }, 500);

        return () => {
            if (phoneCheckTimeoutRef.current) {
                clearTimeout(phoneCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.phone, formData.countryId, isOpen, selectedCountry, recheckTick]);

    const validateForm = () => {
        const newErrors = {};
        const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/; // Letters (incl. accented), spaces, hyphens, and apostrophes
        const addressRegex = /^[A-Za-zÀ-ÿ0-9,.\-\s]+$/; // Letters (incl. accented), numbers, commas, dots, hyphens
        const safeCharsRegex = /^[A-Za-zÀ-ÿ0-9,.\-\s]*$/; // Safe characters for additional info
        const alphaSpaceRegex = /^[A-Za-zÀ-ÿ\s]+$/; // Letters (incl. accented) and spaces

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = t('addPatient.name_is_required');
        } else if (/^\s+$/.test(formData.name)) {
            newErrors.name = t('addPatient.name_is_required');
        } else if (!nameRegex.test(formData.name)) {
            newErrors.name = t('addPatient.invalid_name_format');
        } else if (formData.name.length < 2 || formData.name.length > 20) {
            newErrors.name = t('addPatient.name_length');
        }

        // Surname validation
        if (!formData.surname.trim()) {
            newErrors.surname = t('addPatient.surname_is_required');
        } else if (/^\s+$/.test(formData.surname)) {
            newErrors.surname = t('addPatient.surname_is_required');
        } else if (!nameRegex.test(formData.surname)) {
            newErrors.surname = t('addPatient.invalid_surname_format');
        } else if (formData.surname.length < 2 || formData.surname.length > 20) {
            newErrors.surname = t('addPatient.surname_length');
        }

        // Gender validation
        if (!formData.gender) {
            newErrors.gender = t('addPatient.gender_is_required');
        }

        // DOB validation
        if (!formData.dob) {
            newErrors.dob = t('addPatient.dob_is_required');
        } else if (new Date(formData.dob) > new Date()) {
            newErrors.dob = t('addPatient.dob_cannot_be_future');
        } else if (new Date(formData.dob).getFullYear() < 1900) {
            newErrors.dob = t('addPatient.dob_must_be_after_1900');
        }

        // Occupation validation (optional)
        if (formData.occupation.trim()) {
            if (!alphaSpaceRegex.test(formData.occupation)) {
                newErrors.occupation = t('addPatient.invalid_occupation_format');
            } else if (formData.occupation.length < 2 || formData.occupation.length > 25) {
                newErrors.occupation = t('addPatient.occupation_length');
            }
        }

        // Email validation — mandatory per this hospital's login policy; a
        // filled email is always format/duplicate-checked.
        if (!formData.email.trim()) {
            if (loginPolicy.email.required) {
                newErrors.email = t('addPatient.email_is_required');
            }
        } else {
            // Sanitize email - remove dangerous characters
            const sanitizedEmail = formData.email.replace(/[<>]/g, '');
            const email = sanitizedEmail.trim();

            // Check for whitespace-only
            if (/^\s+$/.test(formData.email)) {
                newErrors.email = t('addPatient.email_is_required');
            }
            // Check total length (RFC 5321: max 254 characters)
            else if (email.length > 254) {
                newErrors.email = t('addPatient.email_too_long');
            } else {
                // Split email into local and domain parts
                const parts = email.split('@');
                if (parts.length !== 2) {
                    newErrors.email = t('addPatient.invalid_email_format');
                } else {
                    const [localPart, domainPart] = parts;
                    // Local part (before @) should be max 64 characters (RFC 5321)
                    if (localPart.length > 64) {
                        newErrors.email = t('addPatient.email_local_part_too_long');
                    } else if (localPart.length === 0) {
                        newErrors.email = t('addPatient.invalid_email_format');
                    } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                        newErrors.email = t('addPatient.invalid_email_format');
                    } else if (domainPart.length > 253) {
                        newErrors.email = t('addPatient.email_domain_part_too_long');
                    } else if (!emailRegex.test(email)) {
                        newErrors.email = t('addPatient.invalid_email_format');
                    } else if (emailStatus === false) {
                        newErrors.email = blockMessage('email');
                    } else if (emailStatus === null && !emailBusy) {
                        newErrors.email = t('addPatient.verify_email_address');
                    }
                    // A missing OTP no longer blocks: handleSubmit confirms
                    // "verify at first sign-in" instead (account starts pending).
                }
            }
        }

        // Phone validation — mandatory per this hospital's login policy
        if (!formData.phone.trim()) {
            if (loginPolicy.mobile.required) {
                newErrors.phone = t('addPatient.phone_is_required');
            }
        } else {
            const phoneMin = selectedCountry?.mobileMinLength || 10;
            const phoneMax = selectedCountry?.mobileMaxLength || 10;
            const phoneRegex = new RegExp(`^\\+?\\d{${phoneMin},${phoneMax}}$`);
            if (!phoneRegex.test(formData.phone)) {
                newErrors.phone = t('addPatient.invalid_phone_number', { length: phoneMin === phoneMax ? phoneMin : `${phoneMin}-${phoneMax}` });
            } else if (phoneStatus === false) {
                newErrors.phone = blockMessage('phone');
            } else if (phoneStatus === null && !phoneBusy) {
                newErrors.phone = t('addPatient.verify_phone_number');
            }
            // A missing OTP no longer blocks: see handleSubmit.
        }

        // 'EITHER' MODE — ANY ONE CONTACT, VERIFIED (owner, 17 Sep 2026).
        // Neither field is starred, so the two rules above cannot fire on a
        // blank; the hospital still needs ONE way to reach the patient.
        if (missingAnyContact(loginPolicy, {
            hasEmail: !!formData.email.trim(), hasPhone: !!formData.phone.trim(),
        })) {
            newErrors.email = t('addPatient.one_contact_required', 'An e-mail or a mobile number is required');
        }

        // Alt Phone validation (optional)
        if (formData.altPhone && formData.altPhone.trim()) {
            const altMin = selectedCountry?.altMinLength || selectedCountry?.mobileMinLength || 10;
            const altMax = selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 10;
            const altPhoneRegex = new RegExp(`^\\+?\\d{${altMin},${altMax}}$`);
            if (!altPhoneRegex.test(formData.altPhone)) {
                newErrors.altPhone = t('addPatient.invalid_alt_phone_number', { length: altMin === altMax ? altMin : `${altMin}-${altMax}` });
            }
        }

        // Address validation
        if (!formData.address.trim()) {
            newErrors.address = t('addPatient.address_is_required');
        } else if (/^\s+$/.test(formData.address)) {
            newErrors.address = t('addPatient.address_is_required');
        } else {
            // Sanitize address - remove dangerous characters
            const sanitizedAddress = formData.address.replace(/[<>]/g, '');
            if (!addressRegex.test(sanitizedAddress)) {
                newErrors.address = t('addPatient.invalid_address_format');
            } else if (sanitizedAddress.length < 5 || sanitizedAddress.length > 100) {
                newErrors.address = t('addPatient.address_length');
            }
        }

        // City validation
        if (!formData.city.trim()) {
            newErrors.city = t('addPatient.city_is_required');
        } else if (/^\s+$/.test(formData.city)) {
            newErrors.city = t('addPatient.city_is_required');
        } else if (!alphaSpaceRegex.test(formData.city)) {
            newErrors.city = t('addPatient.invalid_city_format');
            // Minimum 2, not 5. At five, Pune, Goa, Agra and Ooty could not be
            // typed into an Indian hospital's patient form at all — the
            // receptionist's only way out was to misspell the town. Owner's
            // decision, 11 Sep 2026; the same change was made to the occupation
            // rule above and to both rules in PatientProfileTab.
        } else if (formData.city.length < 2 || formData.city.length > 25) {
            newErrors.city = t('addPatient.city_length');
        }

        // Country validation
        if (!formData.countryId) {
            newErrors.countryId = t('addPatient.country_is_required');
        }

        // Zip Code validation
        if (!formData.zipCode.trim()) {
            newErrors.zipCode = t('addPatient.zipcode_is_required');
        } else {
            const zipMin = selectedCountry?.zipMinLength || 5;
            const zipMax = selectedCountry?.zipMaxLength || 6;
            const zipRegex = new RegExp(`^\\d{${zipMin},${zipMax}}$`);
            if (!zipRegex.test(formData.zipCode)) {
                newErrors.zipCode = t('addPatient.invalid_zipcode_format', { length: zipMin === zipMax ? zipMin : `${zipMin}-${zipMax}` });
            }
        }

        // Additional Information validation
        if (formData.additional) {
            // Sanitize additional - remove dangerous characters
            const sanitizedAdditional = formData.additional.replace(/[<>]/g, '');
            if (/^\s+$/.test(formData.additional)) {
                newErrors.additional = t('addPatient.additional_only_whitespace');
            } else if (sanitizedAdditional.length > 150) {
                newErrors.additional = t('addPatient.additional_length');
            } else if (!safeCharsRegex.test(sanitizedAdditional)) {
                newErrors.additional = t('addPatient.invalid_additional_format');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    function calculateAge(dob) {
        const birthDate = new Date(dob);
        const today = new Date();
        // Use UTC getters for birthDate to avoid timezone shift on DOB
        let age = today.getFullYear() - birthDate.getUTCFullYear();
        const monthDiff = today.getMonth() - birthDate.getUTCMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
            age--;
        }
        return age;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // First name: only the first character uppercased.
        if (name === 'name' && processedValue.length > 0) {
            processedValue = formatFirstName(processedValue);
        }
        // Surname (family name): first character uppercased, rest as typed.
        if (name === 'surname' && processedValue.length > 0) {
            processedValue = formatSurname(processedValue);
        }

        // Sanitize input - remove dangerous characters for text fields
        if (name === 'email' || name === 'address' || name === 'additional' || name === 'altPhone') {
            processedValue = processedValue.replace(/[<>]/g, '');
        }

        // For zipcode, phone, and altPhone, only allow numbers
        if (name === 'zipCode' || name === 'phone' || name === 'altPhone') {
            processedValue = processedValue.replace(/[^0-9]/g, '');
        }

        // Restrict name and surname to letters, spaces, hyphens, and apostrophes only
        if ((name === 'name' || name === 'surname') && processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) {
            return;
        }

        // Restrict email to valid email characters only (letters, numbers, dots, hyphens, underscores, plus, @)
        if (name === 'email' && processedValue) {
            // Allow only valid email characters: letters, numbers, dots, hyphens, underscores, plus, and @
            if (!/^[a-zA-Z0-9._+-@]*$/.test(processedValue)) return;
        }

        // Reset email status and tracking when email changes
        if (name === 'email') {
            setEmailStatus(null);
            setEmailBlock(null);
            cancelInTenantHandoff();
            lastCheckedEmailRef.current = '';
            isEmailCheckInProgressRef.current = false;
            resetOtpEmailState();
        }

        // Reset phone status and tracking when phone changes
        if (name === 'phone') {
            setPhoneStatus(null);
            setPhoneBlock(null);
            cancelInTenantHandoff();
            lastCheckedPhoneRef.current = '';
            isPhoneCheckInProgressRef.current = false;
            resetOtpPhoneState();
        }

        setFormData((prev) => ({
            ...prev,
            [name]: processedValue,
            ...(name === 'dob' && processedValue ? { age: calculateAge(processedValue) } : {}),
            ...(name === 'country' ? { phone: '', zipCode: '' } : {}),
        }));
        setApiError(null);

        if (name === 'dob') {
            let dobError = null;
            if (!processedValue) {
                dobError = t('addPatient.dob_is_required');
            } else if (new Date(processedValue) > new Date()) {
                dobError = t('addPatient.dob_cannot_be_future');
            } else if (new Date(processedValue).getFullYear() < 1900) {
                dobError = t('addPatient.dob_must_be_after_1900');
            }
            setErrors((prev) => ({ ...prev, dob: dobError }));
        } else {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }

        // Check if character limit is reached
        const maxLengths = {
            name: 20,
            surname: 20,
            zipCode: selectedCountry?.zipMaxLength || 10,
            phone: selectedCountry?.mobileMaxLength || 15
        };

        if (maxLengths[name] && processedValue.length >= maxLengths[name]) {
            setCharLimitReached({ ...charLimitReached, [name]: true });
        } else {
            setCharLimitReached({ ...charLimitReached, [name]: false });
        }
    };

    const resetOtpEmailState = () => {
        setEmailOtpSent(false);
        setEmailOtpCode('');
        setEmailOtpVerified(false);
        setEmailOtpError('');
        setEmailOtpTimer(0);
        setEmailProof(null);
        if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
        emailOtpVerifyingRef.current = false;
    };

    const resetOtpPhoneState = () => {
        setPhoneOtpSent(false);
        setPhoneOtpCode('');
        setPhoneOtpVerified(false);
        setPhoneOtpError('');
        setPhoneOtpTimer(0);
        setPhoneProof(null);
        if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        phoneOtpVerifyingRef.current = false;
    };

    const startOtpTimer = (setTimer, timerRef) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setTimer(60);
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) { clearInterval(timerRef.current); return 0; }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSendEmailOtp = async () => {
        setEmailOtpError('');
        try {
            const result = await dispatch(sendOtp({ email: formData.email.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpSent(true);
                setEmailOtpCode('');
                setEmailOtpVerified(false);
                startOtpTimer(setEmailOtpTimer, emailOtpTimerRef);
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('addPatient.failed_to_send_otp'));
        }
    };

    const handleVerifyEmailOtp = useCallback(async (code) => {
        const otpVal = code ?? emailOtpCode;
        if (emailOtpVerifyingRef.current || otpVal.length !== 6) return;
        emailOtpVerifyingRef.current = true;
        setEmailOtpError('');
        try {
            const result = await dispatch(verifyOtp({ email: formData.email.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpVerified(true);
                setEmailProof(result?.proof?.token || null);
                if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
                setEmailOtpTimer(0);
            } else {
                setEmailOtpError(t('addPatient.invalid_otp'));
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('addPatient.invalid_otp'));
        } finally {
            emailOtpVerifyingRef.current = false;
        }
    }, [emailOtpCode, dispatch, axiosInstance, formData.email]);

    const handleSendPhoneOtp = async () => {
        setPhoneOtpError('');
        try {
            const result = await dispatch(sendOtp({ phone: formData.phone.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpSent(true);
                setPhoneOtpCode('');
                setPhoneOtpVerified(false);
                startOtpTimer(setPhoneOtpTimer, phoneOtpTimerRef);
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('addPatient.failed_to_send_otp'));
        }
    };

    const handleVerifyPhoneOtp = useCallback(async (code) => {
        const otpVal = code ?? phoneOtpCode;
        if (phoneOtpVerifyingRef.current || otpVal.length !== 6) return;
        phoneOtpVerifyingRef.current = true;
        setPhoneOtpError('');
        try {
            const result = await dispatch(verifyOtp({ phone: formData.phone.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpVerified(true);
                setPhoneProof(result?.proof?.token || null);
                if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
                setPhoneOtpTimer(0);
            } else {
                setPhoneOtpError(t('addPatient.invalid_otp'));
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('addPatient.invalid_otp'));
        } finally {
            phoneOtpVerifyingRef.current = false;
        }
    }, [phoneOtpCode, dispatch, axiosInstance, formData.phone]);

    // A form that was closed (X, Escape, the parent) must not hand over to a
    // patient 1.6 s later. `isOpen` only hides this component, it never unmounts
    // it, so the unmount cleanup below is not enough on its own.
    useEffect(() => {
        if (isOpen) return;
        if (inTenantHandoffRef.current) { clearTimeout(inTenantHandoffRef.current); inTenantHandoffRef.current = null; }
        setInTenantMatch(null);
    }, [isOpen]);

    // Cleanup OTP timers (and a pending case-A hand-off) on unmount
    useEffect(() => {
        return () => {
            if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
            if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
            if (inTenantHandoffRef.current) clearTimeout(inTenantHandoffRef.current);
        };
    }, []);

    const resetForm = () => {
        setFormData({
            name: '',
            surname: '',
            gender: '',
            dob: '',
            occupation: '',
            email: '',
            phone: '',
            altPhone: '',
            address: '',
            additional: '',
            countryId: '',
            timeZone: '',
            city: '',
            zipCode: '',
            age: '',
        });
        setErrors({});
        setApiError(null);
        setCharLimitReached({});
        setEmailStatus(null);
        setPhoneStatus(null);
        setEmailBlock(null);
        setPhoneBlock(null);
        setInTenantMatch(null);
        setUnclaimedNotice(false);
        setHousehold(null);
        setHouseholdPick(null);
        setHouseholdAdding(false);
        setHouseholdAddError('');
        if (inTenantHandoffRef.current) { clearTimeout(inTenantHandoffRef.current); inTenantHandoffRef.current = null; }
        setMocPopup(null);
        lastLookupRef.current = { email: null, phone: null };
        suppressPopupRef.current = false;
        lastCheckedEmailRef.current = '';
        lastCheckedPhoneRef.current = '';
        isEmailCheckInProgressRef.current = false;
        isPhoneCheckInProgressRef.current = false;
        resetOtpEmailState();
        resetOtpPhoneState();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    // ---- MOC linking (components/moc/MocLinkModal.jsx) ----
    const openMocPopup = (source, lookup) => {
        lastLookupRef.current[source] = lookup;
        // A quiet re-check after the popup changed something must not pop it
        // straight back open — the field's "View" button does that on demand.
        if (suppressPopupRef.current) { suppressPopupRef.current = false; return; }
        setMocPopup({ lookup, source });
    };
    const reopenMocPopup = (source) => {
        const lookup = lastLookupRef.current[source];
        if (lookup) setMocPopup({ lookup, source });
    };
    const blockMessage = (field) => {
        const kind = field === 'email' ? emailBlock : phoneBlock;
        switch (kind) {
            case 'doctor': return t('addPatient.email_used_by_doctor', 'This email is already registered as a doctor account. Please use a different email.');
            case 'moc': return t('addPatient.has_moc_account', 'Has a Mitra One Care profile — add them to this hospital to continue');
            // OPEN PLATFORM: the record exists here but belongs to another
            // doctor. Not a duplicate and not an error — a consent step.
            case 'doctorConsent': return t('addPatient.needs_doctor_consent', 'Ask this patient before adding them to your list');
            case 'pending': return t('addPatient.link_pending', 'A link request is already pending');
            default: return field === 'email' ? t('addPatient.email_already_exists') : t('addPatient.phone_already_exists');
        }
    };
    // What the desk has typed so far. The server keeps it with the consent
    // request and, once the patient confirms, uses it to FILL BLANKS on their
    // person row — never to overwrite what is already there, and not at all
    // once they have signed in themselves (doc/one-patient-row.md §10a.4).
    // Their profile is theirs; this is only what the desk could add to it.
    const buildDraftPatient = () => {
        const d = {};
        ['name', 'surname', 'email', 'phone', 'altPhone', 'dob', 'gender', 'occupation', 'address', 'additional', 'countryId', 'timeZone', 'city', 'zipCode'].forEach((k) => {
            const v = formData[k];
            if (v !== undefined && v !== null && String(v).trim() !== '') d[k] = typeof v === 'string' ? v.trim() : v;
        });
        const lang = localStorage.getItem('i18nextLng');
        if (lang) d.lang = lang;
        return d;
    };
    const handleMocPopupClose = ({ changed } = {}) => {
        const source = mocPopup?.source;
        setMocPopup(null);
        if (changed) {
            // A request was cancelled/created inside the popup — re-check the
            // field quietly so its message reflects the new state.
            suppressPopupRef.current = true;
            if (source === 'phone') { setPhoneStatus(null); setPhoneBlock(null); lastCheckedPhoneRef.current = ''; }
            else { setEmailStatus(null); setEmailBlock(null); lastCheckedEmailRef.current = ''; }
            setRecheckTick((n) => n + 1);
        }
    };
    // Consent given: the server allocated this hospital's number to that
    // person (and filled any blanks from the draft) — nothing left to submit.
    const handleMocLinked = (patient) => {
        setMocPopup(null);
        toast.success(t('addPatient.linked_and_added', 'Added — they now have a patient number here'));
        resetForm();
        if (typeof onSubmit === 'function') onSubmit({ success: true, linked: true, patientId: patient?.patientId || null });
        onClose();
    };
    // Approval channel: async — the patient answers in their app; the desk is
    // told by socket and the request sits in the Pending links list meanwhile.
    const handleMocApprovalSent = () => {
        setMocPopup(null);
        toast.info(t('addPatient.approval_sent_toast', "Request sent to the patient's app — you'll be notified when they answer."));
        resetForm();
        if (typeof onSubmit === 'function') onSubmit({ success: false, pendingLink: true });
        onClose();
    };
    // Case (a): they already have a patient number here → open them instead.
    // This used to be the popup's "Open patient" button; it now runs by itself.
    const handleOpenExistingPatient = (inTenant) => {
        setMocPopup(null);
        resetForm();
        if (typeof onOpenPatient === 'function') onOpenPatient(inTenant);
        onClose();
    };
    const cancelInTenantHandoff = () => {
        if (inTenantHandoffRef.current) { clearTimeout(inTenantHandoffRef.current); inTenantHandoffRef.current = null; }
        setInTenantMatch(null);
        // An edited identifier invalidates the household answer too — it was
        // about the contact that is no longer in the field. So is the
        // "nobody matched" notice: it was an answer about the old value.
        setHousehold(null);
        setHouseholdPick(null);
        setHouseholdAddError('');
        setUnclaimedNotice(false);
    };
    // Ask whether the matched contact is a whole household. Answers null —
    // "carry on exactly as before" — for 404 NO_MATCH, for any other refusal,
    // and for a lone primary with no room to grow, because a one-row radio
    // list decides nothing the old hand-off did not already decide.
    const tryHousehold = async (source, ident) => {
        let action;
        try {
            action = await dispatchRef.current(lookupPatientHousehold({ ...ident, axiosInstance: axiosInstanceRef.current }));
        } catch {
            return null;
        }
        if (!lookupPatientHousehold.fulfilled.match(action)) return null;
        const h = action.payload;
        const members = Array.isArray(h?.members) ? h.members.filter((m) => m?.patientId) : [];
        const rows = (h?.primary?.patientId ? 1 : 0) + members.length;
        if (!rows) return null;
        if (rows === 1 && !h.canAddMember) return null;
        return { ...h, members, source };
    };
    // Raise the picker instead of the silent hand-off. The identifier itself is
    // fine — it just belongs to more than one person — so it stays green and
    // the auto-open timer is cancelled: the desk answers, nothing jumps.
    const showHousehold = (h) => {
        if (inTenantHandoffRef.current) { clearTimeout(inTenantHandoffRef.current); inTenantHandoffRef.current = null; }
        setInTenantMatch(null);
        setUnclaimedNotice(false);
        setHousehold(h);
        setHouseholdPick(null);
        setHouseholdAddError('');
        if (h.source === 'phone') { setPhoneStatus(true); setPhoneBlock(null); }
        else { setEmailStatus(true); setEmailBlock(null); }
    };
    // Picking an existing member is NOT a creation: this person already has a
    // record, so the only thing left to offer is opening it.
    const pickHouseholdMember = (person) => {
        setHouseholdAddError('');
        setHouseholdPick(person?.patientId ? person : null);
    };
    // "Add as a new family member" — no contact fields, the contact is the
    // primary's. On 201 the form closes with the new patient, like any create.
    const submitHouseholdMember = async (values) => {
        const primaryId = household?.primary?.patientId;
        if (!primaryId) return;
        setHouseholdAddError('');
        setHouseholdAdding(true);
        try {
            const action = await dispatchRef.current(addHouseholdMember({
                primaryPatientId: primaryId, ...values, axiosInstance: axiosInstanceRef.current,
            }));
            if (addHouseholdMember.fulfilled.match(action)) {
                const created = action.payload?.patient || action.payload || {};
                if (!created.patientId) {
                    setHouseholdAddError(t('addPatient.household.add_failed', 'Could not add the family member. Please try again.'));
                    return;
                }
                toast.success(t('addPatient.household.member_added', 'Family member added'));
                resetForm();
                if (typeof onSubmit === 'function') onSubmit({ success: true, patientId: created.patientId });
                onClose();
            } else {
                // 404 / 403 / 422 / 409 — the server's own sentence when it
                // sent one, printed on the form like any other refusal.
                const payload = action.payload || {};
                setHouseholdAddError(
                    (typeof payload.message === 'string' && payload.message)
                    || t('addPatient.household.add_failed', 'Could not add the family member. Please try again.')
                );
            }
        } finally {
            setHouseholdAdding(false);
        }
    };
    // Confirm in green under the identifier, then hand over. The short delay is
    // only so the desk reads WHY the form closed — "Change" cancels it, so a
    // mistyped identifier is still correctable.
    const applyInTenantMatch = (source, inTenant) => {
        if (!inTenant?.patientId) return;
        if (inTenantHandoffRef.current) clearTimeout(inTenantHandoffRef.current);
        setInTenantMatch({ source, patient: inTenant });
        inTenantHandoffRef.current = setTimeout(() => {
            inTenantHandoffRef.current = null;
            handleOpenExistingPatient(inTenant);
        }, 1600);
    };
    // The green line. "Yours" only when the server confirmed the pairing —
    // otherwise it states what it actually knows: this hospital already has a
    // patient number for them, so there is nothing to add.
    const inTenantMessage = () => (inTenantMatch?.patient?.linkedToDoctor === true
        ? t('addPatient.already_your_patient', 'This is already your patient — opening their record.')
        : t('addPatient.already_registered_here', 'This person already has a patient number here — opening it.'));
    // Back out: drop the match and clear the identifier that produced it.
    const clearInTenantMatch = (source) => {
        cancelInTenantHandoff();
        if (source === 'phone') {
            setFormData(prev => ({ ...prev, phone: '' }));
            setPhoneStatus(null);
            setPhoneBlock(null);
            lastCheckedPhoneRef.current = '';
            isPhoneCheckInProgressRef.current = false;
            resetOtpPhoneState();
        } else {
            setFormData(prev => ({ ...prev, email: '' }));
            setEmailStatus(null);
            setEmailBlock(null);
            lastCheckedEmailRef.current = '';
            isEmailCheckInProgressRef.current = false;
            resetOtpEmailState();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // A record already exists here — this form is handing over to it.
        if (inTenantMatch) return;
        // The contact belongs to a household: every row on the card already
        // exists, so "Add patient" has nothing to create. The card's own
        // buttons — Open profile, or Add member — are the way out, and saying
        // so beats a button that silently does nothing.
        if (household) {
            toast.warn(t('addPatient.household.choose_required', 'Choose someone from this household, or add a new family member.'));
            return;
        }
        if (!validateForm()) return;

        // A FILLED identifier must at least pass the duplicate check.
        const hasEmail = !!formData.email.trim();
        const hasPhone = !!formData.phone.trim();
        if (hasEmail && emailStatus !== true) {
            toast.warn(t('addPatient.verify_email_to_proceed'));
            return;
        }
        if (hasPhone && phoneStatus !== true) {
            toast.warn(t('addPatient.verify_phone_to_proceed'));
            return;
        }

        // ISOLATED: mandated OTPs may be SKIPPED at the desk (the patient may
        // not be reachable right now) — the account is then created pending,
        // and the patient verifies with an OTP at their first sign-in. Make
        // sure the staff member chose that on purpose.
        // Proof-based: the verified booleans are only UI state — the server
        // accepts nothing but the proof token, so "pending" means "no token".
        // Under 'either' ONE verified contact settles it — the second may be
        // typed and left unverified without making the account pending. Every
        // other mode is unchanged (utils/loginPolicy#otpVerificationPending).
        // CONNECTED: there is nothing to confirm. An unverified contact is the
        // normal case, not a choice — the patient verifies at their first
        // sign-in, which the hint under the contact fields already says.
        const otpStillOwed = !connected && otpVerificationPending(loginPolicy, {
            hasEmail, hasPhone, emailVerified: !!emailProof, phoneVerified: !!phoneProof,
        });
        // Said to the server whenever a given contact goes without a proof, so
        // a backend that still reads the hospital's login policy creates the
        // account pending instead of refusing it.
        const createsUnverified = connected
            ? ((hasEmail && !emailProof) || (hasPhone && !phoneProof))
            : otpStillOwed;
        if (otpStillOwed) {
            const pendingConfirm = await Swal.fire({
                title: t('addPatient.pending_verification_title', 'Verify later?'),
                text: t('addPatient.pending_verification_text', 'Not all required contact details were verified with an OTP. The account will be created, but the patient must verify with a one-time code at their first sign-in before they can use it.'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('addPatient.pending_verification_confirm', 'Create as pending'),
                cancelButtonText: t('addPatient.cancel', 'Cancel'),
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
            });
            if (!pendingConfirm.isConfirmed) {
                return;
            }
        }

        const result = await Swal.fire({
            title: t('addPatient.confirm_title'),
            text: t('addPatient.confirm_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('addPatient.confirm_button', 'Yes, Add Patient'),
            cancelButtonText: t('addPatient.cancel', 'Cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const payload = {
                name: formData.name,
                surname: formData.surname,
                gender: formData.gender,
                dob: formData.dob,
                occupation: formData.occupation,
                email: formData.email,
                phone: formData.phone,
                altPhone: formData.altPhone,
                address: formData.address,
                additional: formData.additional,
                countryId: formData.countryId,
                timeZone: formData.timeZone,
                city: formData.city,
                zipCode: formData.zipCode,
                profileStatus: 'complete',
                // Server-side proof only — the old emailVerified/phoneNoVerified
                // booleans are ignored by the backend and no longer sent.
                ...(emailProof && { emailProof }),
                ...(phoneProof && { phoneProof }),
                ...(createsUnverified && { allowPending: true }),
            };

            // Add doctorId to payload if user is a doctor
            if (userRole === 'doctor') {
                const doctorId = localStorage.getItem('ID');
                if (doctorId) {
                    payload.doctorId = doctorId;
                }
            }

            await dispatch(addPatient(payload)).unwrap();

            toast.success(t('addPatient.success_text'));

            resetForm();
            if (typeof onSubmit === 'function') {
                onSubmit({ success: true });
            }
            onClose();
        } catch (err) {
            const data = err?.response?.data;

            // The person already has a patient number HERE. The lookup above
            // normally catches this before submit; this is the server's last
            // word (e.g. a race with another desk). Not really an error — say
            // who was matched instead of a bare "email already exists".
            if (data?.code === 'PATIENT_ALREADY_REGISTERED') {
                const p = data.patient || {};
                const who = [p.name, p.surname].filter(Boolean).join(' ').trim();
                const msg = t('addPatient.already_registered', {
                    name: who || t('addPatient.this_patient', 'this patient'),
                    defaultValue: `${who || 'This patient'} already has a patient number here (matched by ${data.matchedOn || 'email'}). Nothing to create — open them from the patient list and book directly.`,
                });
                setApiError(msg);
                toast.info(msg);
                return;
            }

            // The server refused a silent create: the contact belongs to a
            // Mitra One Care profile, and a connected hospital never mints a
            // second person for a human who already exists. Re-run the lookup
            // and open the same consent popup the debounced check opens — the
            // desk asks, the patient confirms, this hospital gets a number.
            if (data?.code === 'MOC_CONSENT_REQUIRED') {
                const source = data.matchedOn === 'phone' ? 'phone' : 'email';
                try {
                    const lookupAction = await dispatchRef.current(lookupMocLink(
                        source === 'phone'
                            ? { phone: formData.phone, doctorId: ownDoctorId }
                            : { email: formData.email, doctorId: ownDoctorId }
                    ));
                    if (lookupAction?.payload && !lookupAction.error) {
                        openMocPopup(source, lookupAction.payload);
                        return;
                    }
                } catch { /* fall through to the message below */ }
                const msg = typeof data.message === 'string' ? data.message : t('addPatient.moc_consent_required', 'This contact already belongs to a Mitra One Care profile — ask the patient to confirm before adding them here.');
                setApiError(msg);
                toast.info(msg);
                return;
            }

            // Policy enforcement from the backend: no contact at all
            // (IDENTIFIER_REQUIRED), a required field is missing, or an OTP
            // proof is mandatory (and pending was not allowed).
            const policyMsg = policyErrorMessage(data, t, i18n.language, loginPolicy);
            if (policyMsg) {
                setErrors((prev) => ({ ...prev, [policyErrorField(data.field)]: policyMsg }));
                setApiError(policyMsg);
                toast.warn(policyMsg);
                return;
            }

            const errorMessage =
                data?.message?.en ||
                err?.message ||
                t('addPatient.failed_to_add_patient');

            setApiError(errorMessage);

            toast.warn(errorMessage);
        }
    };

    if (!isOpen) return null;

    return (
        <div data-testid="add-patient-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div data-testid="add-patient-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                {/* Header Section - Sticky */}
                <div data-testid="add-patient-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                    <h3 data-testid="add-patient-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">{t('addPatient.heading')}</h3>
                    <button
                        data-testid="close-add-patient-modal"
                        id="btn-close-modal"
                        onClick={handleClose}
                        className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                    >
                        <X data-testid="close-modal-icon" className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                </div>

                {/* Scrollable Form Content */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                    {apiError && (
                        <div data-testid="add-patient-api-error" className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-xl mb-4 shadow-md">
                            {apiError}
                        </div>
                    )}
                    <form data-testid="add-patient-form" onSubmit={handleSubmit} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${patientLoading ? "opacity-50 pointer-events-none" : ""}`}>
                        <div data-testid="basic-information-section" className="sm:col-span-2 lg:col-span-3">
                            <h4 data-testid="basic-information-title" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-4">{t('addPatient.basic_information')}</h4>
                        </div>
                        {/* 1. Gender */}
                        <div data-testid="gender-field-container">
                            <label htmlFor="gender" data-testid="gender-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.gender')} <span className="text-red-500">*</span></label>
                            <GenderSelect
                                value={formData.gender}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, gender: val }));
                                    setErrors(prev => ({ ...prev, gender: null }));
                                }}
                                options={getGenderOptions(t)}
                                placeholder={t('addPatient.select_gender', 'Select Gender')}
                                error={!!errors.gender}
                            />
                            {errors.gender && <p data-testid="gender-error" className="mt-2 text-xs text-red-500 font-medium">{errors.gender}</p>}
                        </div>
                        {/* 2. First Name */}
                        <div data-testid="name-field-container">
                            <label htmlFor="name" data-testid="name-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.name')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="name-input"
                                    id="input-name"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.name_placeholder', "Enter patient's first name")}
                                    maxLength={20}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.name && <p data-testid="name-error" className="mt-2 text-xs text-red-500 font-medium">{errors.name}</p>}
                            {charLimitReached.name && <p className="mt-2 text-xs text-red-500 font-medium">{t('addPatient.reached_text_limit', { limit: 20 })}</p>}
                        </div>
                        {/* 3. Surname */}
                        <div data-testid="surname-field-container">
                            <label htmlFor="surname" data-testid="surname-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.surname')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.surname ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="surname-input"
                                    id="input-surname"
                                    name="surname"
                                    type="text"
                                    value={formData.surname}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.surname_placeholder', "Enter patient's last name")}
                                    maxLength={20}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.surname && <p data-testid="surname-error" className="mt-2 text-xs text-red-500 font-medium">{errors.surname}</p>}
                            {charLimitReached.surname && <p className="mt-2 text-xs text-red-500 font-medium">{t('addPatient.reached_text_limit', { limit: 20 })}</p>}
                        </div>
                        <div data-testid="dob-field-container">
                            <label htmlFor="dob" data-testid="dob-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.dob')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Calendar className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="dob-input"
                                    id="input-dob"
                                    name="dob"
                                    type="date"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    min="1900-01-01"
                                    max={new Date().toISOString().split('T')[0]}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 text-sm"
                                />
                            </div>
                            {errors.dob && <p data-testid="dob-error" className="mt-2 text-xs text-red-500 font-medium">{errors.dob}</p>}
                        </div>
                        <div data-testid="age-field-container">
                            <label htmlFor="age" data-testid="age-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.age')}</label>
                            <div className="relative flex items-center border rounded-xl p-3 bg-gray-100 border-gray-300">
                                <Calendar className="text-gray-400 w-5 h-5 mr-2" />
                                <input
                                    data-testid="age-input"
                                    id="input-age"
                                    name="age"
                                    type="number"
                                    value={formData.dob && !errors.dob ? calculateAge(formData.dob) : ''}
                                    readOnly
                                    className="w-full bg-transparent focus:outline-none text-gray-800 text-sm cursor-not-allowed"
                                />
                            </div>
                        </div>
                        <div data-testid="occupation-field-container">
                            <label htmlFor="occupation" data-testid="occupation-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.occupation')}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.occupation ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Briefcase className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="occupation-input"
                                    id="input-occupation"
                                    name="occupation"
                                    type="text"
                                    value={formData.occupation}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.occupation_placeholder', "Enter patient's occupation")}
                                    maxLength={25}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.occupation && <p data-testid="occupation-error" className="mt-2 text-xs text-red-500 font-medium">{errors.occupation}</p>}
                        </div>
                        <div data-testid="contact-information-section" className="sm:col-span-2 lg:col-span-3">
                            <div data-testid="section-divider" className="border border-gray-200 h-[1px] my-10"></div>
                            <h4 data-testid="contact-information-title" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-4">{t('addPatient.contact_information')}</h4>
                            {isAnyOneContact(loginPolicy) && (
                                <p data-testid="any-one-contact-hint" className="text-xs text-gray-500 -mt-2">
                                    {connected
                                        ? t('addPatient.moc_contact_hint', 'One contact is enough: an e-mail or a mobile number. Both are welcome, and no code is needed here.')
                                        : t('addPatient.one_contact_hint', 'One contact is enough: fill in the e-mail or the mobile number and verify that one. The other is optional and may stay unverified.')}
                                </p>
                            )}
                        </div>
                        <div data-testid="email-field-container">
                            <label htmlFor="email" data-testid="email-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.email')} {loginPolicy.email.required && <span className="text-red-500">*</span>}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${emailOtpVerified
                                ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                                : emailStatus === false
                                    ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                    : emailStatus === true
                                        ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                                        : errors.email
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                                <input
                                    data-testid="email-input"
                                    id="input-email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.email_placeholder', 'Enter email address')}
                                    maxLength={254}
                                    disabled={emailOtpVerified}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                                {emailBusy && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                                )}
                                {!emailBusy && emailStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, email: '' }));
                                            setEmailStatus(null);
                                            setEmailBlock(null);
                                            setErrors(prev => ({ ...prev, email: null }));
                                            lastCheckedEmailRef.current = '';
                                            resetOtpEmailState();
                                        }}
                                    />
                                )}
                                {/* The Verify offer follows the POLICY, exactly as
                                    AppointmentModal's does: a channel this
                                    hospital does not have us verify must not grow
                                    a button that mints a code nothing will ask
                                    for. Under the platform default both flags are
                                    on, so this changes nothing today — it is the
                                    e-mail-optional-and-unverified tenants it
                                    keeps honest. */}
                                {loginPolicy.email.otp && !emailBusy && emailStatus === true && !emailOtpVerified && (
                                    <button
                                        type="button"
                                        data-testid="add-patient-email-otp-send-button"
                                        title={connected ? t('addPatient.verify_optional_hint', 'Optional — only if the patient is with you now. Otherwise they verify at their first sign-in.') : undefined}
                                        onClick={handleSendEmailOtp}
                                        disabled={sendOtpLoading || emailOtpSent}
                                        className={connected ? OPTIONAL_OTP_CLASS : "ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"}
                                    >
                                        {sendOtpLoading ? '...' : emailOtpSent ? t('addPatient.sent', 'Sent') : (connected ? t('addPatient.verify_optional', 'Verify (optional)') : t('addPatient.send_otp', 'Send OTP'))}
                                    </button>
                                )}
                                {emailOtpVerified && (
                                    <ShieldCheck className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            {errors.email && <p data-testid="email-error" className="mt-2 text-xs text-red-500 font-medium">{errors.email}</p>}
                            {!emailBusy && emailStatus === false && (
                                <p className="text-red-500 text-xs mt-1 flex flex-wrap items-center gap-x-2" data-testid="add-patient-email-blocked">
                                    <span>{blockMessage('email')}</span>
                                    {['moc', 'pending'].includes(emailBlock) && (
                                        <button type="button" onClick={() => reopenMocPopup('email')} className="text-[#2D8A8A] font-semibold hover:underline cursor-pointer" data-testid="add-patient-email-moc-button">
                                            {emailBlock === 'moc' ? t('addPatient.link_account', 'Add to this hospital') : t('addPatient.view_details', 'View')}
                                        </button>
                                    )}
                                </p>
                            )}
                            {/* A record already exists here: confirmed, not blocked. */}
                            {inTenantMatch?.source === 'email' && (
                                <p className="text-green-600 text-xs mt-1 font-medium flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="add-patient-email-in-tenant">
                                    <span className="flex items-center gap-1">
                                        <Check size={12} /> {inTenantMessage()}
                                    </span>
                                    <button type="button" onClick={() => clearInTenantMatch('email')} className="text-[#2D8A8A] font-semibold hover:underline cursor-pointer" data-testid="add-patient-email-change-button">
                                        {t('addPatient.change_identifier', 'Change')}
                                    </button>
                                </p>
                            )}
                            {/* Email OTP input row */}
                            {emailOtpSent && !emailOtpVerified && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            data-testid="add-patient-email-otp-input"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={emailOtpCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setEmailOtpCode(val);
                                                setEmailOtpError('');
                                                if (val.length === 6) handleVerifyEmailOtp(val);
                                            }}
                                            placeholder={t('addPatient.enter_otp', 'Enter OTP')}
                                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        />
                                        <button
                                            type="button"
                                            data-testid="add-patient-email-otp-verify-button"
                                            onClick={() => handleVerifyEmailOtp()}
                                            disabled={verifyOtpLoading || emailOtpCode.length !== 6}
                                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {verifyOtpLoading ? '...' : t('addPatient.verify', 'Verify')}
                                        </button>
                                    </div>
                                    <div>
                                        {emailOtpTimer > 0 ? (
                                            <span className="text-xs text-gray-500">{t('addPatient.resend_in', { seconds: emailOtpTimer })}</span>
                                        ) : (
                                            <button type="button" data-testid="add-patient-email-otp-resend-button" onClick={handleSendEmailOtp} disabled={sendOtpLoading} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">
                                                {t('addPatient.resend_otp', 'Resend OTP')}
                                            </button>
                                        )}
                                    </div>
                                    {emailOtpError && <p className="text-red-500 text-xs">{emailOtpError}</p>}
                                </div>
                            )}
                            {emailOtpVerified && (
                                <p className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('addPatient.email_otp_verified')}</p>
                            )}
                        </div>
                        <div data-testid="phone-field-container">
                            <label htmlFor="phone" data-testid="phone-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.phone')} {loginPolicy.mobile.required && <span className="text-red-500">*</span>}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 ${!formData.countryId ? 'bg-gray-100' : 'bg-white'} ${phoneOtpVerified
                                ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                                : phoneStatus === false
                                    ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                    : phoneStatus === true
                                        ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                                        : errors.phone
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Phone className={`w-5 h-5 mr-2 flex-shrink-0 ${!formData.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
                                <PhoneCountryDropdown
                                    countryTimezoneList={countryTimezoneList}
                                    countryId={formData.countryId}
                                    onSelect={({ countryId, timeZone }) => {
                                        setFormData(prev => ({ ...prev, countryId, timeZone, phone: '', altPhone: '', zipCode: '' }));
                                        setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                        setPhoneStatus(null);
                                        if (lastCheckedPhoneRef) lastCheckedPhoneRef.current = '';
                                        if (resetOtpPhoneState) resetOtpPhoneState();
                                    }}
                                    disabled={phoneOtpVerified}
                                />
                                <input
                                    data-testid="phone-input"
                                    id="input-phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                    maxLength={selectedCountry?.mobileMaxLength || 15}
                                    disabled={!formData.countryId || phoneOtpVerified}
                                    placeholder={!formData.countryId ? t('addPatient.select_country_first', 'Select country first') : t('addPatient.phone_placeholder', { length: selectedCountry?.mobileMinLength === selectedCountry?.mobileMaxLength ? selectedCountry?.mobileMinLength : `${selectedCountry?.mobileMinLength}-${selectedCountry?.mobileMaxLength}` })}
                                    className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!formData.countryId ? 'cursor-not-allowed' : ''}`}
                                />
                                {phoneBusy && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                                )}
                                {!phoneBusy && phoneStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, phone: '' }));
                                            setPhoneStatus(null);
                                            setPhoneBlock(null);
                                            setErrors(prev => ({ ...prev, phone: null }));
                                            lastCheckedPhoneRef.current = '';
                                            resetOtpPhoneState();
                                        }}
                                    />
                                )}
                                {loginPolicy.mobile.otp && !phoneBusy && phoneStatus === true && !phoneOtpVerified && (
                                    <button
                                        type="button"
                                        data-testid="add-patient-phone-otp-send-button"
                                        title={connected ? t('addPatient.verify_optional_hint', 'Optional — only if the patient is with you now. Otherwise they verify at their first sign-in.') : undefined}
                                        onClick={handleSendPhoneOtp}
                                        disabled={sendOtpLoading || phoneOtpSent}
                                        className={connected ? OPTIONAL_OTP_CLASS : "ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"}
                                    >
                                        {sendOtpLoading ? '...' : phoneOtpSent ? t('addPatient.sent', 'Sent') : (connected ? t('addPatient.verify_optional', 'Verify (optional)') : t('addPatient.send_otp', 'Send OTP'))}
                                    </button>
                                )}
                                {phoneOtpVerified && (
                                    <ShieldCheck className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            {errors.phone && <p data-testid="phone-error" className="mt-2 text-xs text-red-500 font-medium">{errors.phone}</p>}
                            {!phoneBusy && phoneStatus === false && (
                                <p className="text-red-500 text-xs mt-1 flex flex-wrap items-center gap-x-2" data-testid="add-patient-phone-blocked">
                                    <span>{blockMessage('phone')}</span>
                                    {['moc', 'pending'].includes(phoneBlock) && (
                                        <button type="button" onClick={() => reopenMocPopup('phone')} className="text-[#2D8A8A] font-semibold hover:underline cursor-pointer" data-testid="add-patient-phone-moc-button">
                                            {phoneBlock === 'moc' ? t('addPatient.link_account', 'Add to this hospital') : t('addPatient.view_details', 'View')}
                                        </button>
                                    )}
                                </p>
                            )}
                            {inTenantMatch?.source === 'phone' && (
                                <p className="text-green-600 text-xs mt-1 font-medium flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="add-patient-phone-in-tenant">
                                    <span className="flex items-center gap-1">
                                        <Check size={12} /> {inTenantMessage()}
                                    </span>
                                    <button type="button" onClick={() => clearInTenantMatch('phone')} className="text-[#2D8A8A] font-semibold hover:underline cursor-pointer" data-testid="add-patient-phone-change-button">
                                        {t('addPatient.change_identifier', 'Change')}
                                    </button>
                                </p>
                            )}
                            {/* Phone OTP input row */}
                            {phoneOtpSent && !phoneOtpVerified && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            data-testid="add-patient-phone-otp-input"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={phoneOtpCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setPhoneOtpCode(val);
                                                setPhoneOtpError('');
                                                if (val.length === 6) handleVerifyPhoneOtp(val);
                                            }}
                                            placeholder={t('addPatient.enter_otp', 'Enter OTP')}
                                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        />
                                        <button
                                            type="button"
                                            data-testid="add-patient-phone-otp-verify-button"
                                            onClick={() => handleVerifyPhoneOtp()}
                                            disabled={verifyOtpLoading || phoneOtpCode.length !== 6}
                                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {verifyOtpLoading ? '...' : t('addPatient.verify', 'Verify')}
                                        </button>
                                    </div>
                                    <div>
                                        {phoneOtpTimer > 0 ? (
                                            <span className="text-xs text-gray-500">{t('addPatient.resend_in', { seconds: phoneOtpTimer })}</span>
                                        ) : (
                                            <button type="button" data-testid="add-patient-phone-otp-resend-button" onClick={handleSendPhoneOtp} disabled={sendOtpLoading} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">
                                                {t('addPatient.resend_otp', 'Resend OTP')}
                                            </button>
                                        )}
                                    </div>
                                    {phoneOtpError && <p className="text-red-500 text-xs">{phoneOtpError}</p>}
                                </div>
                            )}
                            {phoneOtpVerified && (
                                <p className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('addPatient.mobile_otp_verified')}</p>
                            )}
                        </div>
                        {/* CONNECTED: what replaced the "create as pending?"
                            question. Nobody at the desk owes a code; the
                            patient proves their contacts at their first
                            sign-in, and that is said before Save, not after. */}
                        {connected && !household && !inTenantMatch && (
                            <p
                                data-testid="add-patient-first-signin-hint"
                                className="sm:col-span-2 lg:col-span-3 -mt-1 flex items-start gap-2 text-xs text-gray-500"
                            >
                                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#2D8A8A]" />
                                <span>{t('addPatient.first_signin_hint', 'The patient verifies their contact and sets a password at their first sign-in.')}</span>
                            </p>
                        )}
                        {/* CASE (c): NOBODY MATCHED. Saying so is the point —
                            at a connected hospital this form does not create a
                            local record, it creates a PERSON in Mitra One Care
                            with this hospital's number against them. That
                            person is UNCLAIMED until they sign in themselves,
                            and until then this desk can still correct it; after
                            that the profile is theirs and read-only here. Only
                            shown once the identifier came back clean, and never
                            at an isolated clinic (unclaimedNotice). */}
                        {unclaimedNotice && !household && !inTenantMatch && (
                            <div
                                data-testid="add-patient-unclaimed-hint"
                                className="sm:col-span-2 lg:col-span-3 mt-2 flex items-start gap-2 rounded-xl border border-[#4ABDBD]/30 bg-[#4ABDBD]/5 px-3 py-2"
                            >
                                <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-[#2D8A8A]" />
                                <p className="text-xs text-gray-600">
                                    {t('addPatient.unclaimed_hint', 'No Mitra One Care profile matches. Saving creates one for them and gives them a patient number here — it stays editable at this desk until they sign in to the patient app themselves.')}
                                </p>
                            </div>
                        )}
                        {/* ONE CONTACT, SEVERAL PATIENTS — this contact is a
                            whole household, so the question is WHO, not whether
                            to create. Every row already exists; only the last
                            one makes anything new. */}
                        {household && (
                            <HouseholdPicker
                                household={household}
                                tPrefix="addPatient.household"
                                testId="add-patient-household"
                                selectedId={householdPick?.patientId || null}
                                onSelect={pickHouseholdMember}
                                onAddMember={submitHouseholdMember}
                                adding={householdAdding}
                                addError={householdAddError}
                                disabled={patientLoading}
                                footer={householdPick ? (
                                    <div data-testid="add-patient-household-selected" className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2">
                                        <span className="text-sm font-medium text-green-700">
                                            {t('addPatient.household.already_exists', {
                                                name: [householdPick.name, householdPick.surname].filter(Boolean).join(' ').trim(),
                                                defaultValue: '{{name}} already has a record here — nothing to create.',
                                            })}
                                        </span>
                                        <button
                                            type="button"
                                            data-testid="add-patient-household-open-profile"
                                            onClick={() => handleOpenExistingPatient(householdPick)}
                                            className="text-xs px-3 py-2 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all"
                                        >
                                            {t('addPatient.household.open_profile', 'Open profile')}
                                        </button>
                                    </div>
                                ) : null}
                            />
                        )}
                        <div data-testid="alt-phone-field-container">
                            <label htmlFor="altPhone" data-testid="alt-phone-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.alt_phone', 'Alternate mobile no')}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 ${!formData.countryId ? 'bg-gray-100' : 'bg-white'
                                } ${errors.altPhone ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Phone className={`w-5 h-5 mr-2 ${!formData.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
                                <input
                                    data-testid="alt-phone-input"
                                    id="input-altPhone"
                                    name="altPhone"
                                    type="tel"
                                    value={formData.altPhone}
                                    onChange={handleChange}
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    maxLength={selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 15}
                                    disabled={!formData.countryId}
                                    placeholder={!formData.countryId ? t('addPatient.select_country_first', 'Select country first') : t('addPatient.alt_phone_placeholder', { length: (selectedCountry?.altMinLength || selectedCountry?.mobileMinLength) === (selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength) ? (selectedCountry?.altMinLength || selectedCountry?.mobileMinLength) : `${selectedCountry?.altMinLength || selectedCountry?.mobileMinLength}-${selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength}` })}
                                    className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!formData.countryId ? 'cursor-not-allowed' : ''}`}
                                />
                            </div>
                            {errors.altPhone && <p data-testid="alt-phone-error" className="mt-2 text-xs text-red-500 font-medium">{errors.altPhone}</p>}
                        </div>
                        <div data-testid="address-field-container">
                            <label htmlFor="address" data-testid="address-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.address')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.address ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Home className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="address-input"
                                    id="input-address"
                                    name="address"
                                    type="text"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.address_placeholder', 'Enter street address')}
                                    maxLength={100}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.address && <p data-testid="address-error" className="mt-2 text-xs text-red-500 font-medium">{errors.address}</p>}
                        </div>
                        <div data-testid="additional-field-container">
                            <label htmlFor="additional" data-testid="additional-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.additional')}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.additional ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <FileText className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="additional-input"
                                    id="input-additional"
                                    name="additional"
                                    type="text"
                                    value={formData.additional}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.additional_placeholder', 'Enter additional information (optional)')}
                                    maxLength={150}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.additional && <p data-testid="additional-error" className="mt-2 text-xs text-red-500 font-medium">{errors.additional}</p>}
                        </div>
                        {/* Postal code */}
                        <div data-testid="zipcode-field-container">
                            <label htmlFor="zipCode" data-testid="zipcode-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.zipcode')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 ${!formData.countryId ? 'bg-gray-100' : 'bg-white'
                                } ${errors.zipCode ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <MapPin className={`w-5 h-5 mr-2 ${!formData.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
                                <input
                                    data-testid="zipcode-input"
                                    id="input-zipCode"
                                    name="zipCode"
                                    type="text"
                                    value={formData.zipCode}
                                    onChange={handleChange}
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    maxLength={selectedCountry?.zipMaxLength || 10}
                                    disabled={!formData.countryId}
                                    placeholder={!formData.countryId ? t('addPatient.select_country_first', 'Select country first') : t('addPatient.zipcode_placeholder', { length: selectedCountry?.zipMinLength === selectedCountry?.zipMaxLength ? selectedCountry?.zipMinLength : `${selectedCountry?.zipMinLength}-${selectedCountry?.zipMaxLength}` })}
                                    className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!formData.countryId ? 'cursor-not-allowed' : ''}`}
                                />
                            </div>
                            {errors.zipCode && <p data-testid="zipcode-error" className="mt-2 text-xs text-red-500 font-medium">{errors.zipCode}</p>}
                        </div>
                        {/* City */}
                        <div data-testid="city-field-container">
                            <label htmlFor="city" data-testid="city-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.city')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.city ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <MapPin className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="city-input"
                                    id="input-city"
                                    name="city"
                                    type="text"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.city_placeholder', 'Enter city name')}
                                    maxLength={25}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.city && <p data-testid="city-error" className="mt-2 text-xs text-red-500 font-medium">{errors.city}</p>}
                        </div>
                        {/* Country (auto-syncs with phone country code via shared formData.countryId) */}
                        <div data-testid="country-field-container">
                            <label htmlFor="country" data-testid="country-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.country')} <span className="text-red-500">*</span></label>
                            <CountryTimeZoneSelect
                                countryTimezoneList={countryTimezoneList}
                                countryId={formData.countryId}
                                timeZone={formData.timeZone}
                                onChange={({ countryId, timeZone }) => {
                                    setFormData(prev => ({ ...prev, countryId, timeZone, phone: '', altPhone: '', zipCode: '' }));
                                    setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                    setPhoneStatus(null);
                                    if (lastCheckedPhoneRef) lastCheckedPhoneRef.current = '';
                                    if (resetOtpPhoneState) resetOtpPhoneState();
                                }}
                                placeholder={t('addPatient.select_country', 'Select Country')}
                                error={!!errors.countryId || !!errors.timeZone}
                            />
                            {errors.countryId && <p data-testid="country-error" className="mt-2 text-xs text-red-500 font-medium">{errors.countryId}</p>}
                            {errors.timeZone && <p className="mt-2 text-xs text-red-500 font-medium">{errors.timeZone}</p>}
                        </div>
                    </form>
                </div>

                {/* Footer Section - Sticky */}
                <div data-testid="form-actions-container" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                    <button
                        data-testid="cancel-button"
                        id="btn-cancel"
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                        disabled={patientLoading}
                    >
                        {t('addPatient.cancel', 'Cancel')}
                    </button>
                    <button
                        data-testid="submit-button"
                        id="btn-submit"
                        type="submit"
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2"
                        disabled={patientLoading}
                    >
                        {patientLoading && (
                            <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                        )}
                        {patientLoading ? t('addPatient.creating') : t('addPatient.create')}
                    </button>
                </div>
            </div>
            {/* MOC linking popup — only for a Mitra One Care account elsewhere or
                a pending link request; a record that already exists here never
                opens it. */}
            <MocLinkModal
                open={Boolean(mocPopup)}
                lookup={mocPopup?.lookup || null}
                // OPEN PLATFORM: a record that exists here but is not this
                // doctor's patient is a consent step, not a duplicate. Only a
                // DOCTOR filling this form has a list to add to — an admin
                // adds to the hospital's, so no doctor is named and the lookup
                // answers linkedToDoctor null, i.e. the old inline behaviour.
                consentKind={classifyMocLookup(mocPopup?.lookup) === 'doctorConsent' ? 'doctor' : 'link'}
                consentDoctorId={ownDoctorId}
                draftPatient={buildDraftPatient()}
                onUsePatient={handleOpenExistingPatient}
                usePatientLabel={t('addPatient.open_patient', 'Open patient')}
                onLinked={handleMocLinked}
                onApprovalSent={handleMocApprovalSent}
                onClose={handleMocPopupClose}
            />
        </div>
    );
}